import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search } from "lucide-react";

const SearchFilters = ({ onSearch, initialFilters = {} }) => {
  const [filters, setFilters] = useState({
    city: initialFilters.city || "",
    min_price: initialFilters.min_price || "",
    max_price: initialFilters.max_price || "",
    bedrooms: initialFilters.bedrooms || "",
    bathrooms: initialFilters.bathrooms || "",
    property_type: initialFilters.property_type || "",
  });

  const gtaCities = [
    "Toronto",
    "Mississauga",
    "Brampton",
    "Markham",
    "Richmond Hill",
    "Vaughan",
    "Scarborough",
    "North York",
    "Etobicoke",
    "Pickering",
  ];

  const propertyTypes = ["Condo", "Detached", "Semi-detached", "Townhouse"];

  const handleSubmit = (e) => {
    e.preventDefault();
    const cleanFilters = {};
    Object.keys(filters).forEach(key => {
      if (filters[key] !== "" && filters[key] !== null) {
        cleanFilters[key] = filters[key];
      }
    });
    onSearch(cleanFilters);
  };

  const handleReset = () => {
    const resetFilters = {
      city: "",
      min_price: "",
      max_price: "",
      bedrooms: "",
      bathrooms: "",
      property_type: "",
    };
    setFilters(resetFilters);
    onSearch({});
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white p-8 shadow-lg" data-testid="search-filters-form">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div>
          <Label htmlFor="city" className="text-sm uppercase tracking-widest font-semibold mb-2 block">
            City
          </Label>
          <Select value={filters.city} onValueChange={(value) => setFilters({ ...filters, city: value })}>
            <SelectTrigger id="city" className="h-12 rounded-none border-border focus:border-primary" data-testid="city-select">
              <SelectValue placeholder="All Cities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value=" ">All Cities</SelectItem>
              {gtaCities.map((city) => (
                <SelectItem key={city} value={city}>
                  {city}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="min_price" className="text-sm uppercase tracking-widest font-semibold mb-2 block">
            Min Price
          </Label>
          <Input
            id="min_price"
            type="number"
            placeholder="$0"
            value={filters.min_price}
            onChange={(e) => setFilters({ ...filters, min_price: e.target.value })}
            className="h-12 rounded-none border-border focus:border-primary"
            data-testid="min-price-input"
          />
        </div>

        <div>
          <Label htmlFor="max_price" className="text-sm uppercase tracking-widest font-semibold mb-2 block">
            Max Price
          </Label>
          <Input
            id="max_price"
            type="number"
            placeholder="No Max"
            value={filters.max_price}
            onChange={(e) => setFilters({ ...filters, max_price: e.target.value })}
            className="h-12 rounded-none border-border focus:border-primary"
            data-testid="max-price-input"
          />
        </div>

        <div>
          <Label htmlFor="bedrooms" className="text-sm uppercase tracking-widest font-semibold mb-2 block">
            Bedrooms
          </Label>
          <Select value={filters.bedrooms} onValueChange={(value) => setFilters({ ...filters, bedrooms: value })}>
            <SelectTrigger id="bedrooms" className="h-12 rounded-none border-border focus:border-primary" data-testid="bedrooms-select">
              <SelectValue placeholder="Any" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value=" ">Any</SelectItem>
              <SelectItem value="1">1+</SelectItem>
              <SelectItem value="2">2+</SelectItem>
              <SelectItem value="3">3+</SelectItem>
              <SelectItem value="4">4+</SelectItem>
              <SelectItem value="5">5+</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="bathrooms" className="text-sm uppercase tracking-widest font-semibold mb-2 block">
            Bathrooms
          </Label>
          <Select value={filters.bathrooms} onValueChange={(value) => setFilters({ ...filters, bathrooms: value })}>
            <SelectTrigger id="bathrooms" className="h-12 rounded-none border-border focus:border-primary" data-testid="bathrooms-select">
              <SelectValue placeholder="Any" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value=" ">Any</SelectItem>
              <SelectItem value="1">1+</SelectItem>
              <SelectItem value="2">2+</SelectItem>
              <SelectItem value="3">3+</SelectItem>
              <SelectItem value="4">4+</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="property_type" className="text-sm uppercase tracking-widest font-semibold mb-2 block">
            Property Type
          </Label>
          <Select value={filters.property_type} onValueChange={(value) => setFilters({ ...filters, property_type: value })}>
            <SelectTrigger id="property_type" className="h-12 rounded-none border-border focus:border-primary" data-testid="property-type-select">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value=" ">All Types</SelectItem>
              {propertyTypes.map((type) => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex gap-4 mt-8">
        <Button
          type="submit"
          className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 rounded-none uppercase tracking-widest text-sm font-semibold transition-all duration-300"
          data-testid="search-button"
        >
          <Search className="w-4 h-4 mr-2" />
          Search Properties
        </Button>
        <Button
          type="button"
          onClick={handleReset}
          variant="outline"
          className="border-2 border-primary text-primary hover:bg-primary hover:text-white h-12 px-8 rounded-none uppercase tracking-widest text-sm font-semibold transition-all duration-300"
          data-testid="reset-button"
        >
          Reset
        </Button>
      </div>
    </form>
  );
};

export default SearchFilters;